import { Controller } from "@hotwired/stimulus"
import { isNative, platform, plugin } from "../native"

// "Turn on notifications": Web Push (VAPID) in the browser/PWA, FCM token in the native apps.
export default class extends Controller {
  static targets = ["enable", "status"]
  static values = { vapidKey: String, devicesUrl: String }

  async connect() {
    if (isNative()) {
      this.enableTarget.hidden = !plugin("FirebaseMessaging")
    } else if (this.webPushSupported) {
      const registration = await navigator.serviceWorker.getRegistration("/")
      const existing = registration && await registration.pushManager.getSubscription()
      if (existing) this.say("Notifications are on for this device.")
      else this.enableTarget.hidden = false
    } else {
      this.say("This browser can't receive notifications. On iPhone, add the site to your Home Screen first.")
    }
  }

  async enable() {
    this.enableTarget.disabled = true
    try {
      await (isNative() ? this.enableNative() : this.enableWeb())
      this.enableTarget.hidden = true
      this.say("Notifications are on for this device.")
    } catch (error) {
      this.enableTarget.disabled = false
      this.say(error.message || "Couldn't turn notifications on.")
    }
  }

  async enableNative() {
    const messaging = plugin("FirebaseMessaging")
    const { receive } = await messaging.requestPermissions()
    if (receive !== "granted") throw new Error("Notifications are blocked. Allow them in your phone's settings.")
    const { token } = await messaging.getToken()
    await this.register({ platform: platform(), token })
  }

  async enableWeb() {
    if (!this.vapidKeyValue) throw new Error("Notifications aren't set up on this server yet.")
    if ((await Notification.requestPermission()) !== "granted") throw new Error("Notifications are blocked. Allow them in your browser's site settings.")
    const registration = await navigator.serviceWorker.register("/service-worker")
    await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: this.vapidKeyValue })
    const json = subscription.toJSON()
    await this.register({ platform: "web", token: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth })
  }

  async register(device) {
    const response = await fetch(this.devicesUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content },
      credentials: "same-origin",
      body: JSON.stringify({ device })
    })
    if (!response.ok) throw new Error("Couldn't save this device. Please try again.")
  }

  get webPushSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  say(text) { this.statusTarget.textContent = text }
}
